import React, { useCallback, useMemo, useState } from "react";
import {
    Box,
    Tab,
    Tabs,
    Typography,
    IconButton,
    Paper,
    Divider,
    TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDropzone } from "react-dropzone";
import { Controller, useWatch, useFormContext } from "react-hook-form";

const tabStyles = {
    minWidth: 100,
    fontWeight: 500,
};

const dropzoneStyle = {
    border: "2px dashed #ccc",
    padding: "1.5rem",
    textAlign: "center",
    borderRadius: 8,
    backgroundColor: "#f9f9f9",
    cursor: "pointer",
};

const VideoUploadInput = ({
    control,
    name,
    label = "Drop video here or click to upload",
    onDragLabel = "Drop video here",
    acceptedFileTypes = [
        "video/mp4",
        "video/webm",
        "video/ogg",
        "video/x-matroska",
        "video/x-msvideo",
        "video/x-flv",
        "video/mpeg",
        "video/3gpp",
        "video/quicktime",
    ],
}: any) => {
    const { setValue } = useFormContext();
    const [activeTab, setActiveTab] = useState(0);
    const value = useWatch({ control, name });
    const videoUrl = useWatch({ control, name: "video_url" });

    const onDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (acceptedFileTypes.includes(file?.type)) {
                setValue(name, file);
                setValue("video_url", ""); // clear URL if upload happens
            } else {
                alert("Unsupported file type");
                setValue(name, null);
            }
        },
        [acceptedFileTypes, name, setValue],
    );

    const { getRootProps, getInputProps, isDragActive, isDragReject } =
        useDropzone({
            onDrop,
            multiple: false,
            accept: acceptedFileTypes.join(", "),
        });

    const handleDelete = () => {
        setValue(name, null);
        setValue("video_url", "");
    };

    const localURL = useMemo(() => {
        if (value) {
            try {
                return URL.createObjectURL(value);
            } catch {
                return null;
            }
        }
        return null;
    }, [value]);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box display="flex" flexWrap="wrap">
                {/* Preview */}
                <Box
                    sx={{
                        backgroundColor: "white",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        minWidth: 128,
                        padding: "12px",
                    }}
                >
                    {localURL || videoUrl ? (
                        <a
                            href={localURL || videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <video
                                src={localURL || videoUrl}
                                controls
                                style={{
                                    width: 128,
                                    height: 128,
                                    objectFit: "cover",
                                    cursor: "pointer",
                                }}
                            />
                        </a>
                    ) : (
                        <Box
                            width={128}
                            height={128}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            border="1px dashed #ccc"
                            borderRadius={1}
                            color="text.secondary"
                            fontSize={12}
                        >
                            No Video
                        </Box>
                    )}
                </Box>

                {/* Right side (tabs and fields) */}
                <Box flex={1} p={2}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, newTab) => setActiveTab(newTab)}
                        indicatorColor="primary"
                        textColor="primary"
                        sx={{ mb: 2 }}
                    >
                        <Tab label="Upload" sx={tabStyles} />
                        <Tab label="URL" sx={tabStyles} />
                    </Tabs>

                    <Divider sx={{ mb: 2 }} />

                    {activeTab === 0 && (
                        <Box
                            {...getRootProps()}
                            sx={{
                                ...dropzoneStyle,
                                borderColor: isDragReject
                                    ? "error.main"
                                    : isDragActive
                                    ? "primary.main"
                                    : "#ccc",
                            }}
                        >
                            <input {...getInputProps()} />
                            <Typography variant="body2" color="textSecondary">
                                {value ? onDragLabel : label}
                            </Typography>
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Controller
                            name="video_url"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    label="Video URL"
                                    value={field.value || ""}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setValue(name, null); // clear uploaded file if URL is set
                                    }}
                                    fullWidth
                                />
                            )}
                        />
                    )}

                    {(value || videoUrl) && (
                        <Box display="flex" alignItems="center" mt={2}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {value?.name}
                            </Typography>
                            <IconButton onClick={handleDelete} size="small">
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default VideoUploadInput;
